import { useState, useEffect, useRef } from 'react';

export default function useOnScreen(
  ref: React.MutableRefObject<Element | null>,
  rootMargin = '0px',
  debug = false
): boolean {
  const [isIntersecting, setIntersecting] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    // Store the current element value in a variable
    const element = ref.current;

    if (!element) {
      if (debug) console.log('[useOnScreen] No element to observe');
      return;
    }

    if (debug) console.log('[useOnScreen] Setting up observer for element:', element);

    // Create the IntersectionObserver
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (debug) {
          console.log('[useOnScreen] Intersection changed:', {
            isIntersecting: entry.isIntersecting,
            intersectionRatio: entry.intersectionRatio,
            boundingClientRect: entry.boundingClientRect,
            rootBounds: entry.rootBounds,
          });
        }
        setIntersecting(entry.isIntersecting);
      },
      {
        root: null, // Use viewport as root
        rootMargin,
        threshold: 0,
      }
    );

    // Store observer reference
    observerRef.current = observer;

    // Start observing the element
    observer.observe(element);

    // Check initial visibility immediately
    // IntersectionObserver doesn't fire immediately for already-visible elements
    requestAnimationFrame(() => {
      const rect = element.getBoundingClientRect();
      const rootMarginValue = parseInt(rootMargin) || 0;
      const isVisible =
        rect.bottom > -rootMarginValue &&
        rect.top <
          (window.innerHeight || document.documentElement.clientHeight) + rootMarginValue &&
        rect.right > 0 &&
        rect.left < (window.innerWidth || document.documentElement.clientWidth);

      if (debug) {
        console.log('[useOnScreen] Initial visibility check:', {
          isVisible,
          rect,
          windowHeight: window.innerHeight,
          rootMargin: rootMarginValue,
        });
      }

      setIntersecting(isVisible);
    });

    // Handle window resize and orientation change
    const handleViewportChange = () => {
      if (observerRef.current && element) {
        observerRef.current.unobserve(element);
        observerRef.current.observe(element);
      }
    };

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('orientationchange', handleViewportChange);

    // Cleanup function
    return () => {
      if (debug) console.log('[useOnScreen] Cleaning up observer');
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('orientationchange', handleViewportChange);
    };
  }, [ref.current, rootMargin, debug]); // Include ref.current to re-run when element changes

  return isIntersecting;
}
